import {
  SheldonItem,
  VideoValue,
  PlaylistValue
} from '@api/fetchers/prime/types';
import { formatDuration } from '@utils/prime';

export function sheldonItemToCardProps(item: SheldonItem) {
  if (item.contentType === 'video') {
    const video = item.value as VideoValue;
    return {
      title: video.title,
      thumbnailUrl: video.thumbnail,
      footerText: formatDuration(video.duration),
      isLocked: item.isLocked,
      isSpecial: item.isSpecial
    };
  }
  const playlist = item.value as PlaylistValue;
  return {
    title: playlist.name,
    thumbnailUrl: playlist.thumbnail,
    footerText: `${playlist.itemCount} videos`,
    isLocked: item.isLocked,
    isSpecial: item.isSpecial
  };
}

export function playlistItemToEducatorCard(
  item: SheldonItem,
  isLocked: boolean
) {
  const playlist = item.value as PlaylistValue;
  return {
    heroImageUrl: playlist.thumbnail,
    lessons: (playlist.items || []).map((nested) => {
      const video = nested.value as VideoValue;
      return {
        id: video.uid,
        title: video.title,
        durationLabel: formatDuration(video.duration),
        thumbnailUrl: video.thumbnail,
        isLocked
      };
    }),
    variant: 'prime' as const,
    ariaLabel: playlist.name
  };
}

export const formatCompact = (
  startsAt?: string,
  endsAt?: string,
  isLive?: boolean
): string => {
  if (!startsAt) return '';

  try {
    const startDate = new Date(startsAt);
    const endDate = endsAt ? new Date(endsAt) : null;
    const now = new Date();

    const formatTime = (date: Date) =>
      date.toLocaleTimeString('en-IN', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

    const formatDateWithYear = (date: Date) =>
      date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });

    const isSameDay = (d1: Date, d2: Date) =>
      d1.toDateString() === d2.toDateString();

    // Live or started (not ended yet)
    if (isLive || (startDate < now && (endDate === null || endDate > now))) {
      return `Started at ${formatTime(startDate)}, ${formatDateWithYear(
        startDate
      )}`;
    }

    // Ended
    if (endDate && endDate < now) {
      return `Ended at ${formatTime(endDate)}, ${formatDateWithYear(endDate)}`;
    }

    // Upcoming
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    let dayLabel: string;
    if (isSameDay(startDate, today)) {
      dayLabel = 'Today';
    } else if (isSameDay(startDate, tomorrow)) {
      dayLabel = 'Tomorrow';
    } else {
      dayLabel = formatDateWithYear(startDate);
    }

    const endPart = endDate ? ` - ${formatTime(endDate)}` : '';
    return `Starts ${dayLabel}, ${formatTime(startDate)}${endPart}`;
  } catch {
    return '';
  }
};

export const formatViews = (count?: number): string => {
  if (!count && count !== 0) return '';

  if (count >= 1_000_000) return `${Math.floor(count / 1_000_000)}M`;
  if (count >= 1_000) return `${Math.floor(count / 1_000)}K`;
  if (count === 1) return '1';
  return `${count}`;
};
